// eslint-disable-next-line filenames/match-exported
import {
  MarketRow,
  MarketToggle,
  MarketDivider,
  MarketEmptyState,
} from '@market/react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'store';
import { selectPrimaryId, selectIdentifierCollection } from 'store/buyerSlice';
import { IdentifierType } from 'routes/profile/models/Identifier';
import {
  EmailPreference,
  MerchantEmailPreference,
  Product,
  Value,
} from 'routes/profile/models/EmailPreference';
import {
  MarketingOptInSetting,
  isMarketingOptedIn,
} from 'routes/profile/models/MarketingPreferences';
import { BoolFlag } from 'routes/profile/models/Flags';
import { selectBoolFlag } from 'store/featureSlice';
import useIsBlockEmployee from 'routes/profile/hooks/useIsBlockEmployee';
import ModuleLoading from '../common/loading/ModuleLoading';
import ModuleLoadFailed from '../common/errors/ModuleLoadFailed';
import { openToast, ToastVariant } from 'store/toastSlice';
import MerchantMarketingPrefs from './MerchantMarketingPrefs';
import {
  useRetrieveGlobalEmailPreferencesQuery,
  useRetrieveMerchantEmailPreferencesQuery,
  useUpdateEmailPreferenceMutation,
} from 'store/query/api-extensions/receiptAndMarketingPreferences';
import {
  useRetrieveMarketingPreferencesQuery,
  useUpdateMarketingPreferenceMutation,
} from 'store/query/api-extensions/marketingPreferences';
import MarketingEmailDropdown from './MarketingEmailDropdown';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import useCdpPageTracking from 'utils/custom-react-hooks/useCdpPageTracking';
import Page from '../layout/Page';
import {
  actionSelectEmailProperties,
  actionToggleMarketing,
  viewMarketingPreferencesProperties,
} from 'services/tracking/cdp/events/marketingPreferences';
import { useTranslation } from 'react-i18next';
import { buyerportalCdpClient } from 'services/tracking/cdp/clients/buyerportal';

function useMarketingData(selectedEmailId: string | null, useNewApi: boolean) {
  const retrievePreferencesArg = selectedEmailId || skipToken;

  // Old API hooks (skipped when feature flag is on)
  const [updateEmailPreference, updateEmailPrefResult] =
    useUpdateEmailPreferenceMutation();

  const oldGlobal = useRetrieveGlobalEmailPreferencesQuery(
    useNewApi ? skipToken : retrievePreferencesArg
  );

  const oldMerchant = useRetrieveMerchantEmailPreferencesQuery(
    useNewApi ? skipToken : retrievePreferencesArg
  );

  // New API hooks (skipped when feature flag is off)
  const [updateMarketingPreference, updateMarketingPrefResult] =
    useUpdateMarketingPreferenceMutation();

  const newPrefs = useRetrieveMarketingPreferencesQuery(
    useNewApi ? retrievePreferencesArg : skipToken
  );

  const globalEmailPreferences = oldGlobal.data;

  const isQueryLoading = useNewApi
    ? newPrefs.isLoading
    : oldGlobal.isLoading || oldMerchant.isLoading;

  const isError = useNewApi ? newPrefs.isError : oldGlobal.isError;

  const isGlobalMarketingPrefTrue = useNewApi
    ? newPrefs.data
      ? isMarketingOptedIn(newPrefs.data.globalSetting)
      : false
    : globalEmailPreferences?.marketingPreference.value === Value.OptIn;

  const isFetching = useNewApi ? newPrefs.isFetching : oldGlobal.isFetching;

  // Only treat *global* preference updates as page-level loading. Per-seller
  // (merchantId) updates are applied optimistically to the cache and shouldn't
  // unmount the whole merchant list while the request is in flight.
  const isUpdateLoading = useNewApi
    ? updateMarketingPrefResult.isLoading &&
      !updateMarketingPrefResult.originalArgs?.merchantId
    : updateEmailPrefResult.isLoading;

  const merchantPrefs: MerchantEmailPreference[] | undefined = useNewApi
    ? (newPrefs.data?.merchantPreferences ?? []).map(
        (p) =>
          new MerchantEmailPreference({
            cardReceiptPreferences: [],
            marketingPreference: new EmailPreference({
              product: Product.Marketing,
              value: isMarketingOptedIn(p.setting) ? Value.OptIn : Value.OptOut,
            }),
            merchantInfo: {
              displayName: p.merchantDisplayName,
              id: p.merchantId,
              imageUrl: p.merchantImageUrl,
            },
          })
      )
    : oldMerchant.data;

  return {
    globalEmailPreferences,
    isError,
    isFetching,
    isGlobalMarketingPrefTrue,
    isMerchantLoading: oldMerchant.isLoading,
    isNewPrefsFetching: newPrefs.isFetching,
    isQueryLoading,
    isUpdateLoading,
    merchantPrefs,
    updateEmailPreference,
    updateMarketingPreference,
  };
}

const MarketingPreferences: React.FC = () => {
  const { t } = useTranslation();
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);

  const dispatch = useDispatch();

  const isBlockEmployee = useIsBlockEmployee();
  const useNewApi =
    useSelector((state: AppState) =>
      selectBoolFlag(state, BoolFlag.useNewMarketingPreferencesApi)
    ) || isBlockEmployee;

  const primaryEmailId = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (appState: AppState) => selectPrimaryId(appState, IdentifierType.Email)!
  );

  const emailIdentifiers = useSelector((state: AppState) =>
    selectIdentifierCollection(state, IdentifierType.Email)
  );

  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(
    primaryEmailId || emailIdentifiers.at(0)?.token || null
  );

  const {
    globalEmailPreferences,
    isError,
    isFetching,
    isGlobalMarketingPrefTrue,
    isMerchantLoading,
    isNewPrefsFetching,
    isQueryLoading,
    isUpdateLoading,
    merchantPrefs,
    updateEmailPreference,
    updateMarketingPreference,
  } = useMarketingData(selectedEmailId, useNewApi);

  const isLoading = !isInitialLoaded && isQueryLoading;
  const hasVisitedMerchants = merchantPrefs ? merchantPrefs.length > 0 : false;

  useCdpPageTracking({
    didLoadingFail: isError,
    isLoading,
    onLoad: () => setIsInitialLoaded(true),
    properties: viewMarketingPreferencesProperties(
      selectedEmailId || undefined,
      isGlobalMarketingPrefTrue
    ),
  });

  if (isLoading) {
    return <ModuleLoading />;
  }

  if (isError) {
    return <ModuleLoadFailed />;
  }

  const toggleGlobalEmailPreference = (isToggledOn: boolean) => {
    if (useNewApi) {
      if (!selectedEmailId) {
        return;
      }
      updateMarketingPreference({
        emailId: selectedEmailId,
        setting: isToggledOn
          ? MarketingOptInSetting.MARKETING_OPT_IN
          : MarketingOptInSetting.MARKETING_OPT_OUT,
      })
        .unwrap()
        .then(() => {
          dispatch(
            openToast({
              message: t('common.changeSuccess'),
              variant: ToastVariant.Success,
            })
          );
        })
        .catch(() => {
          dispatch(
            openToast({
              message: t('common.somethingWentWrong.retryable.direct'),
              variant: ToastVariant.Critical,
            })
          );
        });
    } else {
      updateEmailPreference({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        emailId: selectedEmailId!,
        preference: new EmailPreference({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          product: globalEmailPreferences!.marketingPreference.product,
          value: isToggledOn ? Value.OptIn : Value.OptOut,
        }),
      })
        .unwrap()
        .then(() => {
          dispatch(
            openToast({
              message: t('common.changeSuccess'),
              variant: ToastVariant.Success,
            })
          );
        })
        .catch(() => {
          dispatch(
            openToast({
              message: t('common.somethingWentWrong.retryable.direct'),
              variant: ToastVariant.Critical,
            })
          );
        });
    }
  };

  const handleNewApiMerchantToggle = async (
    isEnabled: boolean,
    merchantId: string
  ) => {
    if (!selectedEmailId) {
      return;
    }
    await updateMarketingPreference({
      emailId: selectedEmailId,
      merchantId,
      setting: isEnabled
        ? MarketingOptInSetting.MARKETING_OPT_IN
        : MarketingOptInSetting.MARKETING_OPT_OUT,
    })
      .unwrap()
      .then(() => {
        buyerportalCdpClient.track(
          'Toggle Marketing',
          actionToggleMarketing(selectedEmailId, isEnabled, merchantId)
        );
      })
      .catch(() => {
        dispatch(
          openToast({
            message: t('common.somethingWentWrong.retryable.direct'),
            variant: ToastVariant.Critical,
          })
        );
      });
  };

  const renderEmpty = () => {
    return (
      <MarketEmptyState>
        <p slot={'secondary-text'}>
          {t('profile.marketingPreferences.emptyStateText')}
        </p>
      </MarketEmptyState>
    );
  };

  const renderNonEmpty = () => {
    const shouldShowMarketingPrefs =
      isGlobalMarketingPrefTrue && hasVisitedMerchants && !isUpdateLoading;

    return (
      <>
        <div className="my-8">
          <MarketingEmailDropdown
            emailIdentifiers={emailIdentifiers}
            onSelect={(event) => {
              buyerportalCdpClient.track(
                'Select Email',
                actionSelectEmailProperties(event.detail.value as string)
              );
              setSelectedEmailId(event.detail.value as string);
            }}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedEmailId={selectedEmailId!}
          />
        </div>
        <MarketRow
          key={selectedEmailId}
          selected={isGlobalMarketingPrefTrue}
          disabled={isUpdateLoading || isFetching}
          onMarketRowSelected={() => {
            buyerportalCdpClient.track(
              'Toggle Marketing',
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              actionToggleMarketing(selectedEmailId!, true)
            );

            toggleGlobalEmailPreference(true);
          }}
          onMarketRowDeselected={() => {
            buyerportalCdpClient.track(
              'Toggle Marketing',
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              actionToggleMarketing(selectedEmailId!, false)
            );

            toggleGlobalEmailPreference(false);
          }}
        >
          <div id="global-marketing-label">
            <label className="medium-20 my-0">
              {t('profile.marketingPreferences.autoSubscribe')}
            </label>
            <p className="my-0 text-grey">
              {t('profile.marketingPreferences.autoSubscribeExplain')}
            </p>
          </div>
          {/* market-row overwrites a slotted control's aria-label with the
              textContent of its [slot="label"] element — which is undefined
              here — so label via aria-labelledby, which the toggle checks
              first (same pattern as the global receipts toggle). */}
          <MarketToggle
            slot="control"
            aria-labelledby="global-marketing-label"
          />
        </MarketRow>
        {shouldShowMarketingPrefs && (
          <>
            <MarketDivider className="my-8"></MarketDivider>
            <div>
              <h2 className={'heading-20 mt-8 mb-0'}>
                {t('profile.marketingPreferences.merchantEmailTitle')}
              </h2>
              <div className="my-4">
                <p>{t('profile.marketingPreferences.merchantEmailSubtitle')}</p>
              </div>
            </div>
            <MerchantMarketingPrefs
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              emailId={useNewApi ? selectedEmailId! : primaryEmailId}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              merchantPrefs={merchantPrefs!}
              isLoading={useNewApi ? isNewPrefsFetching : isMerchantLoading}
              onRowToggle={useNewApi ? handleNewApiMerchantToggle : undefined}
            />
          </>
        )}
      </>
    );
  };

  return (
    <Page>
      <h1 className={'heading-30 mb-4'}>
        {t('profile.marketingPreferences.title')}
      </h1>
      <p className={'mb-8'}>{t('profile.marketingPreferences.subtitle')}</p>
      {emailIdentifiers.length > 0 ? renderNonEmpty() : renderEmpty()}
    </Page>
  );
};

export default MarketingPreferences;
